/* Signup, Login, Verify, Settings, and a protected-route wrapper for WHO's
   veteran-facing identity surface. Styled with the WHO palette.
*/
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, LogOut, UserCircle2, Lock, Bell, FileCheck2, Info, LifeBuoy, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PilotDemoBanner from "@/components/PilotDemoBanner";
import { useUserAuth } from "@/context/UserAuthContext";

const palette = {
  pageWrap: "min-h-[70vh] bg-cream",
  card: "bg-white border border-borderSubtle rounded-xl shadow-sm p-6 lg:p-8",
  label: "block text-xs uppercase tracking-widest text-navy/60 font-semibold mb-1.5",
  input:
    "w-full px-3 py-2.5 bg-white border border-borderSubtle rounded-md text-navy focus:outline-none focus:border-gold focus:ring-2 focus:ring-gold/30",
  btnGold:
    "inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-deep text-navy font-semibold px-5 py-3 rounded-md transition-colors disabled:opacity-50",
  btnGhost:
    "inline-flex items-center justify-center gap-2 border border-navy/25 hover:border-navy hover:bg-navy hover:text-cream text-navy font-semibold px-4 py-2 rounded-md transition-colors text-sm",
  err: "text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2",
  ok: "text-sm text-green-700 bg-green-50 border border-green-200 rounded-md px-3 py-2",
  hint: "text-xs text-slateSoft",
};

/* ============================== SIGNUP ============================== */
export function Signup() {
  const { user } = useUserAuth();
  if (user) return <Navigate to="/settings" replace />;
  return (
    <div className={palette.pageWrap} data-testid="signup-page">
      <Seo title="Join the WHO pilot" path="/signup" description="WHO is invite-only during the pilot." />
      <PilotDemoBanner text="WHO is invite-only during the pilot." />
      <div className="max-w-xl mx-auto px-6 lg:px-10 py-14">
        <p className="eyebrow text-gold-deep">Invite only</p>
        <h1 className="mt-3 font-display font-black text-navy text-3xl lg:text-4xl">Join the WHO pilot.</h1>
        <p className="mt-4 text-slateSoft">
          WHO is in private pilot. Accounts are created only through administrator-generated,
          single-use invitation links.
        </p>
        <div className={`mt-8 ${palette.card} space-y-4`} data-testid="signup-invite-only">
          <p className="text-navy font-semibold">Have an invitation?</p>
          <p className="text-sm text-slateSoft">
            Open the link your WHO contact shared with you &mdash; it will take you to the
            invite acceptance page where you can set a password and finish signup.
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <Link to="/login" className={palette.btnGold} data-testid="signup-go-login">
              Sign in to an existing account
            </Link>
            <Link to="/contact" className={palette.btnGhost} data-testid="signup-contact-link">
              Request an invitation
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Legacy inline VerifyBlock removed — invite redemption now handles signup end-to-end.

/* ============================== LOGIN ============================== */
const roleHomeFor = (u) => {
  const roles = u?.roles || [];
  if (roles.includes("provider_user")) return "/provider";
  if (roles.includes("who_guide")) return "/guides";
  if (roles.includes("veteran")) return "/my-support";
  return "/settings";
};

export function Login() {
  const { signIn, user } = useUserAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const next = loc.state?.from || (user ? roleHomeFor(user) : "/settings");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={next} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const res = await signIn(email, password);
      nav(loc.state?.from || roleHomeFor(res?.user));
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Unable to sign in.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={palette.pageWrap} data-testid="login-page">
      <Seo title="Sign in · WHO" path="/login" description="Sign in to manage your WHO account." />
      <div className="max-w-md mx-auto px-6 lg:px-10 py-14">
        <p className="eyebrow text-gold-deep">Sign in</p>
        <h1 className="mt-3 font-display font-black text-navy text-3xl lg:text-4xl">Welcome back.</h1>
        <form onSubmit={submit} className={`mt-8 ${palette.card} space-y-4`} data-testid="login-form">
          {err && <p className={palette.err} data-testid="login-error">{err}</p>}
          <div>
            <label className={palette.label} htmlFor="li-email">Email</label>
            <input id="li-email" data-testid="login-email" required type="email" className={palette.input} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className={palette.label} htmlFor="li-pass">Password</label>
            <input id="li-pass" data-testid="login-password" required type="password" className={palette.input} value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div className="pt-2 flex items-center justify-between">
            <button className={palette.btnGold} type="submit" disabled={busy} data-testid="login-submit">
              {busy ? "Signing in…" : "Sign in"}
            </button>
            <Link to="/signup" className="text-sm text-navy underline underline-offset-2">Create account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

/* =============================== SETTINGS =============================== */
export function Settings() {
  const { user, authHeaders, API, signOut, refresh } = useUserAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [data, setData] = useState({ consents: [], events: [], preferences: [], current_versions: {} });
  const [status, setStatus] = useState("");

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([
      axios.get(`${API}/me/consents`, { headers: authHeaders() }),
      axios.get(`${API}/me/preferences`, { headers: authHeaders() }),
    ]);
    setData({
      consents: c.data.consents || [],
      events: c.data.events || [],
      current_versions: c.data.current_versions || {},
      preferences: p.data.preferences || [],
    });
  }, [API, authHeaders]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (!user) return <Navigate to="/login" state={{ from: "/settings" }} replace />;

  const flash = (msg) => {
    setStatus(msg);
    setTimeout(() => setStatus(""), 2500);
  };

  const withdraw = async (consent_id) => {
    await axios.post(`${API}/me/consents/withdraw`, { consent_id }, { headers: authHeaders() });
    await load();
    flash("Consent withdrawn.");
  };

  const grant = async (consent_type) => {
    await axios.post(`${API}/me/consents/grant`, { consent_type }, { headers: authHeaders() });
    await load();
    flash("Consent recorded.");
  };

  const togglePref = async (channel, notification_category, enabled) => {
    await axios.put(`${API}/me/preferences`, { channel, notification_category, enabled }, { headers: authHeaders() });
    await load();
    flash("Preferences updated.");
  };

  const changePw = async (current_password, new_password) => {
    try {
      await axios.patch(`${API}/auth/me/password`, { current_password, new_password }, { headers: authHeaders() });
      flash("Password updated.");
    } catch (ex) {
      flash(ex?.response?.data?.detail || "Could not update password.");
    }
  };

  return (
    <div className={palette.pageWrap} data-testid="settings-page">
      <Seo title="Settings · WHO" path="/settings" description="Manage your WHO account, communication preferences, and consents." />
      <PilotDemoBanner />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow text-gold-deep">Account</p>
            <h1 className="mt-2 font-display font-black text-navy text-3xl lg:text-4xl">Settings</h1>
            <p className="mt-2 text-slateSoft">Signed in as <strong className="text-navy">{user.email}</strong> · roles: {user.roles.join(", ")}</p>
            {/* Role-based workspace shortcuts */}
            <div className="mt-3 flex flex-wrap gap-2" data-testid="workspace-links">
              {(user.roles.includes("veteran") || user.roles.includes("family")) && (
                <Link to="/my-support" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-gold/15 text-gold-deep border border-gold/40 rounded-full px-3 py-1.5 hover:bg-gold/25 transition-colors" data-testid="link-my-support">My Support →</Link>
              )}
              {user.roles.includes("who_guide") && (
                <Link to="/guides" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-navy text-cream rounded-full px-3 py-1.5 hover:bg-navy-deep transition-colors" data-testid="link-guides">WHO Guides™ Workspace →</Link>
              )}
              {user.roles.includes("provider_user") && (
                <Link to="/provider" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-navy text-cream rounded-full px-3 py-1.5 hover:bg-navy-deep transition-colors" data-testid="link-provider">Provider Dashboard →</Link>
              )}
              {user.roles.includes("administrator") && (
                <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-navy text-cream rounded-full px-3 py-1.5 hover:bg-navy-deep transition-colors" data-testid="link-admin">Admin →</Link>
              )}
            </div>
          </div>
          <button className={palette.btnGhost} onClick={async () => { await signOut(); nav("/"); }} data-testid="settings-logout-btn">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>

        {status && <p className={`mt-6 ${palette.ok}`} data-testid="settings-flash">{status}</p>}

        {/* Tab nav */}
        <div className="mt-8 flex flex-wrap gap-2 border-b border-borderSubtle" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              data-testid={`settings-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px ${
                tab === t.key
                  ? "border-gold text-navy"
                  : "border-transparent text-slateSoft hover:text-navy"
              }`}
            >
              <t.Icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "profile" && <ProfileTab user={user} />}
          {tab === "security" && <SecurityTab onChange={changePw} />}
          {tab === "preferences" && <PreferencesTab preferences={data.preferences} onToggle={togglePref} />}
          {tab === "consents" && (
            <ConsentsTab consents={data.consents} events={data.events} versions={data.current_versions} onGrant={grant} onWithdraw={withdraw} />
          )}
          {tab === "privacy" && <PrivacyTab />}
          {tab === "support" && <SupportTab />}
        </div>
      </div>
    </div>
  );
}

const TABS = [
  { key: "profile", label: "Profile", Icon: UserCircle2 },
  { key: "security", label: "Security", Icon: Lock },
  { key: "preferences", label: "Communications", Icon: Bell },
  { key: "consents", label: "Consents", Icon: FileCheck2 },
  { key: "privacy", label: "Privacy", Icon: ShieldCheck },
  { key: "support", label: "Support", Icon: LifeBuoy },
];

function ProfileTab({ user }) {
  return (
    <div className={`${palette.card}`} data-testid="profile-tab">
      <h2 className="font-display font-bold text-navy text-xl">Profile</h2>
      <dl className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-5">
        <Field label="Name">{user.display_name || "—"}</Field>
        <Field label="Email">{user.email}</Field>
        <Field label="Account status">{user.account_status.replace(/_/g, " ")}</Field>
        <Field label="Email verified">{user.email_verified_at ? new Date(user.email_verified_at).toLocaleString() : "Not verified"}</Field>
        <Field label="Roles">{user.roles.join(", ")}</Field>
        <Field label="Member since">{new Date(user.created_at).toLocaleDateString()}</Field>
      </dl>
      <p className="mt-6 text-xs text-slateSoft">To change your name, contact WHO support below. Email changes are not available in the pilot.</p>
    </div>
  );
}

function SecurityTab({ onChange }) {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  return (
    <div className={`${palette.card} space-y-4`} data-testid="security-tab">
      <h2 className="font-display font-bold text-navy text-xl">Change password</h2>
      <div>
        <label className={palette.label}>Current password</label>
        <input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className={palette.input} data-testid="pw-current" />
      </div>
      <div>
        <label className={palette.label}>New password (min 12 chars)</label>
        <input type="password" minLength={12} value={nw} onChange={(e) => setNw(e.target.value)} className={palette.input} data-testid="pw-new" />
      </div>
      <button
        className={palette.btnGold}
        onClick={() => { onChange(cur, nw); setCur(""); setNw(""); }}
        disabled={!cur || nw.length < 12}
        data-testid="pw-submit"
      >
        Update password
      </button>
    </div>
  );
}

function PreferencesTab({ preferences, onToggle }) {
  // Phase 1 default notification categories. Real referral flows ship in Phase 3.
  const categories = [
    { key: "referral_status", label: "Referral & handoff status updates", required: true },
    { key: "appointment_reminders", label: "Appointment reminders" },
    { key: "weekly_digest", label: "Weekly summary digest" },
  ];
  const channels = [
    { key: "email", label: "Email" },
    { key: "sms", label: "SMS" },
    { key: "in_app", label: "In-app" },
  ];
  const isOn = (channel, cat) => {
    const p = preferences.find((p) => p.channel === channel && p.notification_category === cat);
    return p ? !!p.enabled : cat === "referral_status"; // required default-on
  };
  return (
    <div className={`${palette.card}`} data-testid="preferences-tab">
      <h2 className="font-display font-bold text-navy text-xl">Communication preferences</h2>
      <p className="mt-2 text-sm text-slateSoft">
        Toggle how you want WHO to reach you. <strong className="text-navy">Required</strong> notifications
        inform you about the warm handoffs you've started — you can switch channels but can&rsquo;t turn them
        off entirely.
      </p>
      <div className="mt-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-navy/60">
              <th className="py-2 pr-4">Category</th>
              {channels.map((c) => <th key={c.key} className="py-2 px-3 text-center">{c.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <tr key={cat.key} className="border-t border-borderSubtle">
                <td className="py-3 pr-4 text-navy">
                  <div>{cat.label}</div>
                  {cat.required && <span className="text-[10px] uppercase tracking-widest text-gold-deep font-bold">Required</span>}
                </td>
                {channels.map((c) => (
                  <td key={c.key} className="py-3 px-3 text-center">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-gold-deep"
                      checked={isOn(c.key, cat.key)}
                      onChange={(e) => onToggle(c.key, cat.key, e.target.checked)}
                      data-testid={`pref-${cat.key}-${c.key}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConsentsTab({ consents, events, versions, onGrant, onWithdraw }) {
  const required = ["terms_of_service", "privacy_notice"];
  const optional = ["email_communications", "sms_communications", "ai_assisted_processing"];
  const findActive = (t) => consents.find((c) => c.consent_type === t && c.status === "granted");

  const row = (t, lbl, note) => {
    const active = findActive(t);
    return (
      <div key={t} className="flex items-start justify-between gap-4 py-3 border-b border-borderSubtle last:border-0" data-testid={`consent-row-${t}`}>
        <div>
          <p className="text-navy font-semibold">{lbl}</p>
          <p className="text-xs text-slateSoft">{note}</p>
          {active && (
            <p className="mt-1 text-xs text-slateSoft">
              Granted {new Date(active.granted_at).toLocaleString()} · version {active.consent_version}
            </p>
          )}
        </div>
        <div className="shrink-0">
          {active ? (
            <button className={palette.btnGhost} onClick={() => onWithdraw(active.id)} data-testid={`consent-withdraw-${t}`}>Withdraw</button>
          ) : (
            <button className={palette.btnGold + " px-4 py-2 text-sm"} onClick={() => onGrant(t)} data-testid={`consent-grant-${t}`}>Grant</button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="consents-tab">
      <div className="lg:col-span-3 space-y-6">
        <div className={palette.card}>
          <h2 className="font-display font-bold text-navy text-xl">Required service consents</h2>
          <p className="mt-2 text-sm text-slateSoft">Needed to use WHO. Withdrawing a required consent will end your access.</p>
          <div className="mt-4">
            {row("terms_of_service", "Terms of Service", "WHO's terms governing use of the service.")}
            {row("privacy_notice", "Privacy Notice", "How WHO collects, uses, and protects your information.")}
          </div>
        </div>
        <div className={palette.card}>
          <h2 className="font-display font-bold text-navy text-xl">Optional permissions</h2>
          <p className="mt-2 text-sm text-slateSoft">Each is granted and withdrawn separately. Marketing consent is never bundled with service access.</p>
          <div className="mt-4">
            {row("email_communications", "Service email", "WHO may contact you by email about your warm handoffs.")}
            {row("sms_communications", "Service SMS", "WHO may contact you by SMS about your warm handoffs.")}
            {row("ai_assisted_processing", "AI-assisted processing", "Allow WHO Guides™ to use AI tools alongside human review when helping coordinate your care.")}
          </div>
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className={palette.card}>
          <h2 className="font-display font-bold text-navy text-xl">Consent history</h2>
          <p className="mt-2 text-sm text-slateSoft">An append-only record of changes. Withdrawals never erase prior grants.</p>
          <ul className="mt-4 space-y-3 max-h-[480px] overflow-y-auto pr-1" data-testid="consent-events">
            {events.length === 0 && <li className="text-sm text-slateSoft">No history yet.</li>}
            {events.map((e) => (
              <li key={e.id} className="text-sm border-l-2 border-gold pl-3">
                <p className="text-navy font-semibold">
                  {e.event_kind} &middot; {e.consent_type.replace(/_/g, " ")}
                </p>
                <p className="text-xs text-slateSoft">{new Date(e.event_at).toLocaleString()} · v{e.consent_version}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function PrivacyTab() {
  return (
    <div className={palette.card} data-testid="privacy-tab">
      <h2 className="font-display font-bold text-navy text-xl flex items-center gap-2">
        <Info className="w-5 h-5 text-gold-deep" /> How WHO handles your information
      </h2>
      <ul className="mt-4 space-y-3 text-sm text-slateSoft list-disc ml-5">
        <li>Your personal information is used only to coordinate the warm handoffs you initiate.</li>
        <li>WHO never sells your information to advertisers or data brokers.</li>
        <li>Consent is granted per purpose, versioned, and can be withdrawn at any time.</li>
        <li>AI-assisted processing happens only when you grant that specific permission, and is always paired with human review by a WHO Guide.</li>
        <li>You can request an export or deletion of your data via the Support tab.</li>
      </ul>
      <p className="mt-6 text-xs text-slateSoft">This pilot does <strong>not</strong> yet make representations of HIPAA or SOC 2 compliance. WHO is in private pilot with provisional language pending formal legal and security review.</p>
    </div>
  );
}

function SupportTab() {
  return (
    <div className={palette.card} data-testid="support-tab">
      <h2 className="font-display font-bold text-navy text-xl">Support &amp; data rights</h2>
      <p className="mt-2 text-sm text-slateSoft">
        To request a copy of your data, correct inaccurate information, or close your account,
        contact WHO directly. During the pilot these requests are handled manually.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <a href="mailto:support@warmhandoffhq.com" className={palette.btnGold} data-testid="support-email-btn">
          Email WHO support
        </a>
        <Link to="/contact" className={palette.btnGhost} data-testid="support-contact-link">
          Open contact form
        </Link>
      </div>
    </div>
  );
}

/* ============================== shared bits ============================== */
function Field({ label, children }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-widest text-navy/60 font-semibold">{label}</dt>
      <dd className="mt-1 text-navy">{children}</dd>
    </div>
  );
}

function ConsentCheck({ id, testid, checked, onChange, children }) {
  return (
    <label htmlFor={id} className="flex items-start gap-3 text-sm text-navy">
      <input
        id={id}
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 accent-gold-deep"
      />
      <span>{children}</span>
    </label>
  );
}

function ConsentLink({ name }) {
  return (
    <a href="/sources#consent" className="underline underline-offset-2 text-navy font-semibold">
      {name}
    </a>
  );
}

function RequiredChip() {
  return <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-gold-deep font-bold"><Check className="w-3 h-3" />Required</span>;
}
function OptionalChip() {
  return <span className="ml-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-navy/50 font-bold">Optional</span>;
}
